import { usePermissions, useTranslate } from "../../../../customHooks";
import { useMemo, useCallback, useRef, useEffect } from "react";
import { FormLayout, Input, Checkbox, RadioGroup, SubmitButton } from "@panwds/react-form";
import { useForm, FormProvider, Controller, useWatch } from "react-hook-form";
import { isValidIPAMPoolId } from "../../../../utils/validate";
import { Banner, Button, LoadingPanel, Subtitle } from "@panwds/react-ui";
import { useUpdateFirewallMutation } from "../../../../redux/services/firewalls-service";
import { extractFieldsWithConfig } from "../../../../utils/utils";
import { isEmpty } from "lodash";
import AWSServiceIPsTableComponent from "../Edit/EgressNatComponents/AWSServiceIPsTableComponent";
import { toast } from "../../../../components";
import { FirewallUpdateBanner } from "../../FirewallUpdateBanner";
import fwStyles from "../../FirewallEdit.module.scss";
import TabPageHeader from "./TabPageHeader";
import { determinePolicyManaged, POLICY_MANAGER, isFirewallUpdating } from "../../firewallsUtil";

const FirewallEgressNat = (props: { canEdit: boolean, firewallData: Record<string, any>; isLoading: boolean; refreshFirewallData: () => void; featureFlags?: Record<string, any> }) => {
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const [updateFirewall] = useUpdateFirewallMutation();
    const region = useMemo(() => new URLSearchParams(location.search).get('region'), [location.search]);
    const firewallUpdating = isFirewallUpdating(props?.firewallData?.Status?.FirewallStatus);
    const policyMangedBy = determinePolicyManaged(props?.firewallData);
    const egressNatSCMEnabled = props?.featureFlags?.EgressNatSCM === true;
    // Check if egress NAT is currently enabled in the firewall
    const isEgressNatCurrentlyEnabled = props?.firewallData?.Firewall?.EgressNAT?.Enabled === true;
    // Disable IP pool settings when feature flag is off (allow only toggling checkbox)
    const disableIPPoolSettings = policyMangedBy === POLICY_MANAGER.STRATA && !egressNatSCMEnabled;

    const config = { Firewall: ["FirewallId", "Region", "EgressNAT", "UpdateToken", "DeploymentUpdateToken"], Status: ["PublicIPs"] };

    const transformFormDefaultValue = useMemo(() => {
        if (isEmpty(props?.firewallData)) {
            return undefined;
        }

        const newStateWithConfig: any = extractFieldsWithConfig(props?.firewallData, config);

        const updatedFirewall = {
            ...newStateWithConfig.Firewall,
            EgressNAT: {
                ...newStateWithConfig.Firewall?.EgressNAT,
                Settings: {
                    ...newStateWithConfig.Firewall?.EgressNAT?.Settings,
                    IPPoolType: newStateWithConfig.Firewall?.EgressNAT?.Settings?.IPPoolType || 'AWSService',
                    IPAMPoolId: newStateWithConfig.Firewall?.EgressNAT?.Settings?.IPAMPoolId
                }
            },
        };

        return {
            Firewall: updatedFirewall,
            Status: {
                ...newStateWithConfig?.Status,
            }
        };
    }, [props?.firewallData]);

    const formMethods = useForm({
        defaultValues: transformFormDefaultValue,
        mode: 'onBlur',
    });

    const { watch, formState: { errors, dirtyFields }, reset } = formMethods;
    const egressNatEnable = watch("Firewall.EgressNAT.Enabled");
    const iPoolType = useWatch({ control: formMethods.control, name: "Firewall.EgressNAT.Settings.IPPoolType" });
    // Ref to store the initial IPPoolType value
    const initialIPPoolTypeRef = useRef(formMethods.getValues("Firewall.EgressNAT.Settings.IPPoolType"));
    // Check if egress NAT was initially enabled in the API response
    const wasEgressNatInitiallyEnabled = props?.firewallData?.Firewall?.EgressNAT?.Enabled === true;

    useEffect(() => {
        initialIPPoolTypeRef.current = formMethods.getValues("Firewall.EgressNAT.Settings.IPPoolType");
        // Reset form when firewall data is refreshed
        if (transformFormDefaultValue) {
            reset(transformFormDefaultValue);
        }
    }, [transformFormDefaultValue, reset]);

    const transformFormSubmitValue = useCallback((formData: Record<string, any>) => {
        if (isEmpty(formData)) {
            return;
        }

        const { Firewall, Status } = formData;

        let transformedData = {
            ...Firewall,
            EgressNAT: {
                ...Firewall?.EgressNAT
            }
        };

        if (Status && Status?.PublicIPs) {
            transformedData.Status = {
                PublicIPs: Status?.PublicIPs,
            };
        }

        if (!Firewall?.EgressNAT?.Enabled) {
            delete transformedData?.EgressNAT?.Settings;
            delete transformedData?.Status;
        } else if (Firewall?.EgressNAT?.Settings?.IPPoolType === 'AWSService') {
            delete transformedData?.EgressNAT?.Settings?.IPAMPoolId;
        } else if (transformedData?.Status === undefined || Firewall?.EgressNAT?.Settings?.IPPoolType !== 'AWSService') {
            delete transformedData?.Status;
        }

        return transformedData;
    }, []);

    const onSubmit = useCallback(async (data) => {
        try {
            const result = await updateFirewall({
                payload: transformFormSubmitValue(data),
                region: region || '',
            }).unwrap();

            // Refetch the latest firewall data
            await props.refreshFirewallData();

            toast.success("Firewall updated successfully");
        } catch (error: any) {
            if(error?.code === 409) {
                await props.refreshFirewallData();
            }
            toast.error(`${error?.code}: ${error?.error}`, { toastId: "update-firewall-egress-nat" });
        }
    }, [updateFirewall, transformFormSubmitValue, region, props.refreshFirewallData, reset, transformFormDefaultValue]);

    const handleCancel = () => {
        reset(transformFormDefaultValue);
    };

    const awsServiceOptions = [
        { value: 'AWSService', label: translate('resources.firewallsV2.awsService') },
        { value: 'BYOIP', label: translate('resources.firewallsV2.byoip') }
    ];

    const renderPolicyManagedBanner = () => {
        if(policyMangedBy === POLICY_MANAGER.STRATA && !egressNatSCMEnabled && !isEgressNatCurrentlyEnabled) {
            return (
                <Banner type="inline" appearance="standard" showIcon>
                    {translate('resources.firewallsV2.userid.policyManagedEgressNatByBanner')}
                </Banner>
            );
        }
    }

    return (
        <div className="tw-relative">
            {props?.isLoading &&
                <div className="tw-absolute tw-inset-0 tw-right-0 tw-flex tw-justify-center tw-items-center tw-backdrop-blur-sm tw-z-50" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                    <div className="tw-w-6 tw-h-6">
                        <LoadingPanel />
                    </div>
                </div>
            }
            {!props?.canEdit &&
                <FirewallUpdateBanner content={translate('resources.firewallsV2.cantEditFirewall')} />
            }
            {firewallUpdating &&
                <FirewallUpdateBanner refresh={props.refreshFirewallData} />
            }
            <TabPageHeader title={translate(`resources.firewallsV2.egressNat`)} addClassName="tw-pb-4" />
            <div className={(!props?.canEdit || firewallUpdating || (policyMangedBy === POLICY_MANAGER.STRATA && !egressNatSCMEnabled && !isEgressNatCurrentlyEnabled) || !permissions?.UpdateFirewall) ? fwStyles.fwReadOnly : ""}>
                {renderPolicyManagedBanner()}
                <FormProvider {...formMethods}>
                    <form onSubmit={formMethods.handleSubmit(onSubmit)}>
                        <FormLayout>
                            <Checkbox
                                name="Firewall.EgressNAT.Enabled"
                                label={translate(`resources.firewallsV2.enableEgressNat`)}
                                dataMetrics={"cloudngfw-firewall-edit-egressNatEnabled"}
                                description={translate(`resources.firewallsV2.enableEgressNatMutedText`)}
                            />

                            {egressNatEnable && (
                                <>
                                    <Subtitle as={"div"} appearance={"semibold"} size={"sm"}>
                                        {translate(`resources.firewallsV2.ipAddressPool`)}
                                    </Subtitle>
                                    <RadioGroup
                                        name="Firewall.EgressNAT.Settings.IPPoolType"
                                        horizontal={true}
                                        disabled={disableIPPoolSettings}
                                        items={awsServiceOptions?.map(awsService => ({
                                            value: awsService?.value,
                                            children: awsService?.label
                                        }))}
                                    />
                                    {iPoolType === 'BYOIP' && (
                                        <Input
                                            name="Firewall.EgressNAT.Settings.IPAMPoolId"
                                            label={translate(`resources.firewallsV2.ipAddressPool`)}
                                            placeholder="Please enter a IPAM pool id (e.g., ipam-pool-0123456789abcdef)"
                                            register={{ validate: (value) => isValidIPAMPoolId(value) }}
                                            error={errors.Firewall?.EgressNAT?.Settings?.IPAMPoolId?.message}
                                            dataMetrics="cloudngfw-firewall-create-egress-nat-public-own-ips"
                                            disabled={disableIPPoolSettings}
                                        />
                                    )}
                                    {(() => {
                                        // Only show table if egress NAT was initially enabled (not stale data)
                                        if (!wasEgressNatInitiallyEnabled) {
                                            return null;
                                        }
                                        const publicIPs = formMethods.getValues("Status.PublicIPs") || [];
                                        const filteredIPs = publicIPs.filter((ip: any) => ip?.IPSource === iPoolType);
                                        return !isEmpty(filteredIPs) && (
                                            <AWSServiceIPsTableComponent
                                                value={filteredIPs}
                                            />
                                        );
                                    })()}
                                </>
                            )}
                            <div className={fwStyles.formFooterDivider}/>
                            <div className="tw-flex tw-justify-end">
                                <Button onClick={handleCancel} disabled={isEmpty(dirtyFields)} style={{ marginRight: '5px' }}>
                                    {translate(`generic.cancel`)}
                                </Button>
                                <SubmitButton type="submit" disabled={isEmpty(dirtyFields)}>
                                    {translate(`generic.save`)}
                                </SubmitButton>
                            </div>
                        </FormLayout>
                    </form>
                </FormProvider>
            </div>
        </div>
    );
};

export default FirewallEgressNat;
